import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Camera, Sparkles, Activity, Trophy, Users, Mic, Flame, ScanLine, BarChart3, Heart } from "lucide-react";

const Promo = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Timeline configuration
  useEffect(() => {
    const timeline = [
      { step: 0, duration: 2000 }, // "Snap to track your dedications!"
      { step: 1, duration: 2500 }, // "🍎 AI macros counter..."
      { step: 2, duration: 2000 }, // "Image, voice, text"
      { step: 3, duration: 2000 }, // "🌈 Beautiful progress charts"
      { step: 4, duration: 1500 }, // "track anything"
      { step: 5, duration: 2000 }, // "🟥 Share progress pics"
      { step: 6, duration: 2000 }, // "🏆 Climb leaderboards"
      { step: 7, duration: 3000 }, // "👀 Get followers..."
      { step: 8, duration: 100 }   // Reset
    ];

    let currentTimeout: NodeJS.Timeout;
    let accumulatedTime = 0;

    const runTimeline = () => {
      accumulatedTime = 0;
      timeline.forEach(({ step: s, duration }) => {
        currentTimeout = setTimeout(() => {
          if (s === 8) {
            setStep(0);
            runTimeline();
          } else {
            setStep(s);
          }
        }, accumulatedTime);
        accumulatedTime += duration;
      });
    };

    runTimeline();
    return () => clearTimeout(currentTimeout);
  }, []);

  // Animation Logic
  const getPhoneAnimation = (s: number) => {
    const base = { rotateX: 0, rotateY: 0, scale: 1, x: 0, y: 0 };
    const fastTransition = { duration: 0.6, ease: [0.16, 1, 0.3, 1] }; // Faster transition
    
    switch(s) {
      case 0: // Snap to track
        return { animate: { ...base, y: 0, scale: 1.1 }, transition: fastTransition };
      
      case 1: // AI Macros
        return { animate: { ...base, scale: 1.1, rotateX: 0 }, transition: fastTransition };
      
      case 2: // Image, voice, text
        return { animate: { ...base, scale: 1.2, rotateY: 10 }, transition: fastTransition };
      
      case 3: // Charts
        return {
            animate: { ...base, scale: 1.4, y: 100, rotateY: -5 },
            transition: fastTransition
        };

      case 4: // Track anything
        return {
            animate: { ...base, scale: 1.8, y: -100, rotateY: -5 },
            transition: fastTransition
        };

      case 5: // Share pics
        return { animate: { ...base, scale: 1.3, rotateY: 5, rotateZ: -2 }, transition: fastTransition };

      case 6: // Leaderboards (Grid)
        return {
            animate: { ...base, scale: 2.2, y: -150, rotateX: 10 },
            transition: fastTransition
        };

      case 7: // Social
        return { animate: { ...base, scale: 1.1, x: -50, rotateY: 15 }, transition: fastTransition };

      default: return { animate: base, transition: fastTransition };
    }
  };

  const anim = getPhoneAnimation(step);

  return (
    <div className="h-screen w-full bg-black text-white overflow-hidden flex flex-col items-center justify-center relative font-sans selection:bg-white/20">
      
      {/* Dynamic Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            background: step === 1 
              ? "radial-gradient(circle at 50% 50%, #22c55e40 0%, transparent 60%)" // Green for AI
              : step === 3
                ? "radial-gradient(circle at 50% 50%, #a855f740 0%, transparent 60%)" // Purple for Charts
                : step === 7
                  ? "radial-gradient(circle at 50% 50%, #ef444440 0%, transparent 60%)" // Red for Social
                  : "radial-gradient(circle at 50% 50%, #3b82f640 0%, transparent 60%)" // Blue default
          }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        />
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{ 
            backgroundImage: "linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)",
            backgroundSize: "50px 50px" 
          }} 
        />
      </div>

      {/* Text Overlay Layer */}
      <div className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none p-4">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="t0"
              initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
              className="text-center"
            >
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter text-white drop-shadow-2xl mb-4">{t('ui.text.snap')}</h1>
              <p className="text-2xl md:text-4xl font-bold text-white/80">
                to track your dedications!
              </p>
            </motion.div>
          )}
          {step === 1 && (
            <motion.div
              key="t1"
              initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
              className="absolute top-20 md:top-32 text-center max-w-4xl"
            >
              <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight drop-shadow-xl">
                🍎 AI macros counter,<br/>fitness & activity recorder
              </h2>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div
              key="t2"
              initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: 50, filter: "blur(10px)" }}
              className="absolute left-8 md:left-20 top-1/3 text-left"
            >
              <h2 className="text-6xl md:text-8xl font-black text-white leading-none">{t('ui.text.image')}<br/>{t('ui.text.voice')}<br/>{t('ui.text.text')}</h2>
            </motion.div>
          )}
          {step === 3 && (
            <motion.div
              key="t3"
              initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.2, filter: "blur(10px)" }}
              className="absolute bottom-20 text-center"
            >
              <h2 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                🌈 Beautiful progress charts
              </h2>
            </motion.div>
          )}
          {step === 4 && (
            <motion.div
              key="t4"
              initial={{ opacity: 0, scale: 1.5, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.5, filter: "blur(10px)" }}
              className="absolute top-1/3 right-10 text-right"
            >
              <h2 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tighter">{t('ui.text.track')}<br/>{t('ui.text.anything')}</h2>
            </motion.div>
          )}
          {step === 5 && (
            <motion.div
              key="t5"
              initial={{ opacity: 0, rotate: -5 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 5 }}
              className="absolute top-20 left-10"
            >
              <div className="bg-red-500 text-white px-6 py-3 rounded-full font-bold text-2xl md:text-4xl shadow-xl transform -rotate-3">
                {t('pages.promo.share_progress_pics')}
              </div>
            </motion.div>
          )}
          {step === 6 && (
            <motion.div
              key="t6"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="absolute bottom-32 text-center"
            >
              <h2 className="text-5xl md:text-7xl font-black text-yellow-400 drop-shadow-lg">
                {t('pages.promo.climb_leaderboards')}
              </h2>
            </motion.div>
          )}
          {step === 7 && (
            <motion.div
              key="t7"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute left-10 top-1/3 flex flex-col gap-6"
            >
              <div className="flex items-center gap-4 text-3xl md:text-5xl font-bold text-white">
                <span>{t('pages.promo.get_followers')}</span>
              </div>
              <div className="flex items-center gap-4 text-3xl md:text-5xl font-bold text-white">
                <span>{t('pages.promo.share')}</span>
              </div>
              <div className="flex items-center gap-4 text-3xl md:text-5xl font-bold text-white">
                <span>{t('pages.promo.react')}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 3D Phone Stage */}
      <div className="relative w-full h-full flex items-center justify-center perspective-[2000px] z-20">
        <motion.div
          className="relative w-[300px] md:w-[360px] aspect-[9/19.5]"
          style={{ transformStyle: "preserve-3d" }}
          animate={anim.animate}
          transition={anim.transition}
        >
          {/* Phone Container */}
          <div className="absolute inset-0 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden rounded-[3rem] bg-black">
            <div className="relative w-full h-full">
              <AnimatePresence mode="wait">
                
                {/* Step 0: Snap Intro */}
                {step === 0 && (
                  <motion.img
                    key="intro"
                    src="/iphone-screenshots/image-before.png"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Step 1: AI Macros */}
                {step === 1 && (
                  <motion.div
                    key="ai"
                    className="w-full h-full relative overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <img src="/iphone-screenshots/run-results.png" className="w-full h-full object-cover brightness-50" />
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/50 to-transparent z-10"
                      initial={{ top: "-100%" }}
                      animate={{ top: "200%" }}
                      transition={{ duration: 1.5, ease: "linear", repeat: Infinity }}
                    />
                    <div className="absolute top-1/3 left-8 bg-green-500 text-black text-xs font-bold px-2 py-1 rounded">{t('ui.text.protein_45g')}</div>
                    <div className="absolute bottom-1/3 right-8 bg-green-500 text-black text-xs font-bold px-2 py-1 rounded">{t('ui.text.calories_650')}</div>
                  </motion.div>
                )}

                {/* Step 2: Inputs */}
                {step === 2 && (
                  <motion.img
                    key="inputs"
                    src="/iphone-screenshots/ai-text-entry.png"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Step 3: Charts */}
                {step === 3 && (
                  <motion.img
                    key="charts"
                    src="/iphone-screenshots/dashboard-aaron.png"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Step 4: Track Anything */}
                {step === 4 && (
                  <motion.img
                    key="track"
                    src="/iphone-screenshots/track-page.png"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Step 5: Progress Pics */}
                {step === 5 && (
                  <motion.div
                    key="pics"
                    className="w-full h-full relative bg-white"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                     <div className="absolute inset-4 bg-gray-100 overflow-visible">
                        <img src="/iphone-screenshots/track-run.png" className="w-full h-full object-cover" />
                     </div>
                     <div className="absolute bottom-8 left-0 right-0 text-center">
                        <p className="text-black font-handwriting text-xl font-bold">{t('ui.text.progress')}</p>
                     </div>
                  </motion.div>
                )}

                {/* Step 6: Leaderboards */}
                {step === 6 && (
                  <motion.div
                    key="leaderboard"
                    className="w-full h-full bg-zinc-950 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ContributionGrid />
                  </motion.div>
                )}

                {/* Step 7: Social */}
                {step === 7 && (
                  <motion.img
                    key="social"
                    src="/iphone-screenshots/dashboard-aaron.png"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full object-cover blur-sm"
                  />
                )}

              </AnimatePresence>
            </div>
          </div>

          {/* Social Popups (Step 7) */}
          <AnimatePresence>
            {step === 7 && (
              <>
                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 80, y: -100 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-1/3 right-0 bg-zinc-900 p-4 rounded-xl border border-white/10 flex items-center gap-3"
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-xs">JD</div>
                  <div className="text-xs text-white">
                    <span className="font-bold">{t('forms.placeholders.john_doe')}</span> followed you
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 80, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: 0.2 }}
                  className="absolute top-1/2 right-0 bg-zinc-900 p-4 rounded-xl border border-white/10 flex items-center gap-3"
                >
                  <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                  <div className="text-xs text-white">
                    <span className="font-bold">Sarah</span> liked your run
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

        </motion.div>
      </div>
      
      {/* CTA */}
      <div className="absolute bottom-10 z-50">
        <button
          onClick={() => navigate("/auth?signup=true")}
          className="px-10 py-4 bg-white text-black rounded-full font-black text-xl hover:scale-105 transition-transform shadow-2xl"
        >{t('ui.text.get_started')}</button>
      </div>
    </div>
  );
};

const ContributionGrid = () => {
  return (
    <div className="grid grid-cols-7 gap-1 p-4 scale-125">
      {Array.from({ length: 49 }).map((_, i) => {
        const intensity = Math.random() > 0.7 ? 3 : Math.random() > 0.4 ? 2 : Math.random() > 0.2 ? 1 : 0;
        const colors = ["bg-zinc-800", "bg-green-900", "bg-green-700", "bg-green-500"];
        return (
          <motion.div
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: i * 0.005 }}
            className={`w-8 h-8 rounded-sm ${colors[intensity]}`}
          />
        );
      })}
    </div>
  );
};

export default Promo;